<script setup>
import { computed, onMounted, ref } from 'vue'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { useCamerasStore } from '@/store/cameras'
import { useCameraName } from '@/composables/cameraHelpers'
import { humanReadableDuration, humanReadableTimestamp } from '@/composables/datetime'
import { useRoute, useRouter } from 'vue-router'
import { useDownloadFile } from '@/composables/downloadFile'
import { DateTime } from 'luxon'
import { dateTimeInPast, inputRequiredRule } from '@/composables/inputRules'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import CameraSelect from '@/components/CameraSelect.vue'
import _ from 'lodash'

const toast = useToast()
const { t } = useI18n()
const props = defineProps({
  useQueryString: { type: Boolean, default: true },
  prePickedCamera: { type: String, default: null },
  cameraReadOnly: { type: Boolean, default: false },
  timestampPickerMode: { type: Boolean, default: false },
})

const emit = defineEmits(['pickTimestamp'])

const camerasStore = useCamerasStore()
const router = useRouter()
const route = useRoute()
const intervals = [
  1000,
  1000 * 10,
  1000 * 30,
  1000 * 60,
  1000 * 60 * 5,
  1000 * 60 * 10,
  1000 * 60 * 30,
  1000 * 60 * 60,
  1000 * 60 * 60 * 3,
  1000 * 60 * 60 * 6,
  1000 * 60 * 60 * 12,
  1000 * 60 * 60 * 24,
  1000 * 60 * 60 * 24 * 7,
]
const frameCount = 25

const camera = ref(null)
const timestamp = ref(DateTime.now().minus({ minutes: 10 }))
const interval = ref(1000 * 60 * 60)
const highQualityFrames = ref([])
const lowQualityFrames = ref([])
const highQuality = ref(false)
const displayFrame = ref(null)
const form = ref(null)

const isLoading = computed(() => {
  return frameStore.value.value[camera.value?.cameraId]?.[timestamp.value.toMillis()]?.loading
})

const isLoaded = ref(false)

const canSkipForward = computed(() => {
  return timestamp.value.plus({ millisecond: interval.value }).toMillis() <= DateTime.now().toMillis()
})

const frameStore = computed(() => {
  return highQuality.value ? highQualityFrames : lowQualityFrames
})

const availableIntervals = computed(() => {
  return intervals.map(interval => ({ value: interval, title: humanReadableDuration(interval) }))
})

const updateQuery = async () => {
  await updateQueryParams()
  return validateAndLoad()
}

async function updateQueryParams() {
  if (!props.useQueryString || !isLoaded.value) return
  const query = {
    cameraId: camera.value?.cameraId,
    timestamp: timestamp.value.toMillis(),
    highQuality: highQuality.value,
  }
  router.replace({ query })
}

const debouncedJumpBackward = _.debounce(() => {
  updateQuery()
}, 200)

const debouncedJumpForward = _.debounce(() => {
  updateQuery()
}, 200)

async function jumpBackward() {
  timestamp.value = timestamp.value.minus({ millisecond: interval.value })
  debouncedJumpBackward()
}

async function jumpForward() {
  timestamp.value = timestamp.value.plus({ millisecond: interval.value })
  debouncedJumpForward()
}

async function downloadFrame() {
  if (!displayFrame.value) return
  if (!highQuality.value) {
    highQuality.value = true
    await loadFrame()
    highQuality.value = false
  }
  useDownloadFile(displayFrame.value, useCameraName(camera.value.cameraId) + '_' + timestamp.value.toISO() + '.jpg')
}

function evictFromHolder(holder) {
  if (!holder.value) return
  const entries = []
  for (const cameraId of Object.keys(holder.value)) {
    for (const timestamp of Object.keys(holder.value[cameraId])) {
      entries.push({
        cameraId: cameraId,
        timestamp: Number.parseInt(timestamp),
        loadedTimestamp: holder.value[cameraId][timestamp].loadedTimestamp,
      })
    }
  }
  if (entries.length <= frameCount) return
  entries.sort((a, b) => a.loadedTimestamp - b.loadedTimestamp)
  const toDelete = entries.slice(0, entries.length - frameCount)
  for (const entry of toDelete) {
    delete holder.value[entry.cameraId][entry.timestamp]
  }
}

function evictFrames() {
  evictFromHolder(highQualityFrames)
  evictFromHolder(lowQualityFrames)
}

async function validateAndLoad() {
  await form.value.validate()
  if (!form.value.isValid) {
    toast.error(t('toast.fix_errors_above'))
    return
  }

  return loadFrame()
}

async function loadFrame() {
  if (!camera.value || !timestamp.value) return

  const cameraId = camera.value.cameraId
  const frameStoreList = Object.keys(frameStore.value.value)
  if (!frameStoreList.includes(cameraId)) {
    frameStore.value.value[cameraId] = {}
  }

  const timestampInMillis = timestamp.value.toMillis()
  const cameraEntries = frameStore.value.value[cameraId]
  if (cameraEntries[timestampInMillis]?.frame) {
    displayFrame.value = cameraEntries[timestampInMillis].frame
    return
  }

  cameraEntries[timestampInMillis] = { loading: true }
  const frame = await camerasStore.loadFrameForTimestamp(cameraId, timestampInMillis, highQuality.value ? 1080 : 150, true)
  if (!frame) {
    delete cameraEntries[timestampInMillis]
    displayFrame.value = null
    evictFrames()
    return
  }

  cameraEntries[timestampInMillis] = {
    frame: frame,
    loadedTimestamp: Date.now(),
    loading: false,
  }
  displayFrame.value = frame
  evictFrames()
}

onMounted(async () => {
  isLoaded.value = false
  await camerasStore.ensureCamerasLoaded()
  if (props.prePickedCamera) {
    camera.value = camerasStore.getCameraById(undefined, props.prePickedCamera)
  }
  if (!props.useQueryString) return
  const queryParams = route.query
  if (queryParams.cameraId) {
    camera.value = camerasStore.getCameraById(undefined, queryParams.cameraId)
  }
  if (queryParams.timestamp) {
    timestamp.value = DateTime.fromMillis(Number.parseInt(queryParams.timestamp))
  }
  if (queryParams.highQuality) {
    highQuality.value = queryParams.highQuality === 'true'
  }
  isLoaded.value = true
  updateQuery()
})
</script>

<template>
  <div class="py-8 px-4 d-flex flex-column ga-4">
    <v-layout-card>
      <v-card-text>
        <v-form ref="form">
          <v-row
            class="justify-center align-center"
            density="comfortable"
            :no-gutters="true"
          >
            <v-col cols="auto">
              <CameraSelect
                v-model:selected-cameras="camera"
                :read-only-selection="props.cameraReadOnly"
                :multiple="false"
                pre-select-first-available-camera
                @update:selected-cameras="() => updateQuery()"
              />
            </v-col>
            <v-col cols="auto">
              <DateTimePicker
                v-model="timestamp"
                :hide-details="true"
                :max="DateTime.now()"
                class="ma-2"
                :rules="[dateTimeInPast, inputRequiredRule]"
                @update:model-value="() => updateQuery()"
              />
            </v-col>
            <v-col cols="auto">
              <v-switch
                v-model="highQuality"
                :hide-details="true"
                class="ma-2"
                :label="$t('frame_seek.high_quality')"
                @update:model-value="() => updateQuery()"
              />
            </v-col>
            <v-col cols="auto">
              <v-autocomplete
                v-model="interval"
                :hide-details="true"
                variant="outlined"
                class="ma-2"
                style="min-width: 200px"
                :label="$t('frame_seek.interval')"
                :items="availableIntervals"
                @update:model-value="() => updateQuery()"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-layout-card>
    <v-layout-card>
      <v-card-text>
        <v-row
          v-if="camera && timestamp"
          class="justify-center"
        >
          <v-col
            :cols="12"
            class="justify-center"
          >
            <h1
              v-if="displayFrame"
              class="text-center"
            >
              {{ camera.name }} {{ humanReadableTimestamp(timestamp.toMillis()) }}
            </h1>
          </v-col>
          <v-col
            v-if="!displayFrame && !isLoading && isLoaded"
            :cols="12"
          >
            <v-alert type="error">
              {{ $t('frame_seek.could_not_load') }}
            </v-alert>
          </v-col>

          <v-col :cols="12">
            <v-img
              height="50vh"
              :cover="false"
              :src="displayFrame"
              class="justify-center align-center"
            >
              <v-row
                v-if="!displayFrame && !isLoading"
                class="justify-center"
              >
                <v-icon
                  color="red"
                  size="30vh"
                  icon="mdi-video-off"
                />
              </v-row>
              <v-row
                v-if="isLoading"
                class="w-100 justify-center"
              >
                <v-progress-circular
                  size="100"
                  width="10"
                  :indeterminate="true"
                />
              </v-row>
            </v-img>
          </v-col>

          <v-col
            v-if="displayFrame"
            :cols="12"
          >
            <v-row class="justify-center">
              <v-btn
                color="success"
                variant="elevated"
                @click="downloadFrame"
              >
                {{ $t('general_interface.buttons.download') }}
              </v-btn>
            </v-row>
          </v-col>

          <v-row class="justify-center align-center my-4">
            <v-btn
              prepend-icon="mdi-arrow-left"
              :rounded="true"
              class="ma-2"
              color="primary"
              variant="elevated"
              @click="jumpBackward"
            >
              {{ $t('general_interface.buttons.backward') }}
            </v-btn>
            <v-btn
              :disabled="!canSkipForward"
              append-icon="mdi-arrow-right"
              :rounded="true"
              class="ma-2"
              color="primary"
              variant="elevated"
              @click="jumpForward"
            >
              {{ $t('general_interface.buttons.forward') }}
            </v-btn>
          </v-row>
          <v-col
            v-if="props.timestampPickerMode"
            :cols="12"
          >
            <v-btn
              :block="true"
              variant="flat"
              color="primary"
              @click="emit('pickTimestamp', timestamp)"
            >
              {{ $t('general_interface.date_time_picker.confirm') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-layout-card>
  </div>
</template>
